import {
  useCreateTirageMouvement,
  useEditTirageMouvement,
  type TirageMouvement,
} from "@/api/tirages/tirage-mouvement.api";
import * as AF from "@/components/form-items/AbstractForm";
import {
  CancelButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import { Form } from "@/components/ui/form";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  description: z.string(),
  lieu: z.string(),
  dateDepart: z.string(),
  dateRetour: z.string(),
  constatEtatDepart: z.boolean(),
  constatEtatRetour: z.boolean(),
  notes: z.string(),
});

type TirageMouvementForm = z.infer<typeof formSchema>;

const emptyValues: TirageMouvementForm = {
  description: "",
  lieu: "",
  dateDepart: "",
  dateRetour: "",
  constatEtatDepart: false,
  constatEtatRetour: false,
  notes: "",
};

const toFormValues = (mouvement: TirageMouvement): TirageMouvementForm => ({
  description: mouvement.description,
  lieu: mouvement.lieu,
  dateDepart: mouvement.dateDepart,
  dateRetour: mouvement.dateRetour,
  constatEtatDepart: mouvement.constatEtatDepart,
  constatEtatRetour: mouvement.constatEtatRetour,
  notes: mouvement.notes,
});

export type CreateOrEditTirageMouvementProps = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  tirageId: string;
  mouvement?: TirageMouvement;
  readOnly?: boolean;
  disabled?: boolean;
};

export const CreateOrEditTirageMouvement = ({
  open,
  onOpenChange,
  tirageId,
  mouvement,
  readOnly = false,
  disabled = false,
}: CreateOrEditTirageMouvementProps) => {
  const { t } = useTranslation();
  const isEdit = mouvement !== undefined;

  const creator = useCreateTirageMouvement();
  const updater = useEditTirageMouvement();

  const form = useForm<TirageMouvementForm>({
    defaultValues: isEdit ? toFormValues(mouvement) : emptyValues,
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (open) {
      form.reset(mouvement ? toFormValues(mouvement) : emptyValues);
    }
  }, [open, mouvement, form]);

  const onSubmit = (values: TirageMouvementForm) => {
    if (readOnly) return;

    if (isEdit) {
      updater.mutate(
        {
          mouvementId: mouvement.id,
          tirageId,
          mouvement: values,
        },
        {
          onSuccess: () => {
            toast({
              title: t("successUpdateTirageMouvement"),
              variant: "success",
            });
            onOpenChange?.(false);
          },
          onError: () => {
            toast({
              title: t("errorUpdateTirageMouvement"),
              variant: "destructive",
            });
          },
        },
      );
      return;
    }

    creator.mutate(
      { tirageId, ...values },
      {
        onSuccess: () => {
          toast({
            title: t("successCreateTirageMouvement"),
            variant: "success",
          });
          form.reset();
          onOpenChange?.(false);
        },
        onError: () => {
          toast({
            title: t("errorCreateTirageMouvement"),
            variant: "destructive",
          });
        },
      },
    );
  };

  const title = readOnly
    ? t("view.tirage.mouvement.title")
    : isEdit
      ? t("edit.tirage.mouvement.title")
      : t("add.tirage.mouvement.title");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl w-full px-0 flex flex-col max-h-screen overflow-y-auto">
        <Form {...form}>
          <fieldset
            disabled={readOnly}
            className="flex flex-col flex-1 border-0 p-0 m-0 min-h-0"
          >
            <form
              className="flex flex-col flex-1 min-h-0"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <SheetHeader className="px-6 pt-6 pb-2">
                <SheetTitle>{title}</SheetTitle>
              </SheetHeader>
              <AF.Col className="px-6 py-4 flex-1 overflow-y-auto">
                <AF.Text
                  name="description"
                  label={t("tirage.mouvement.description")}
                />
                <AF.Text name="lieu" label={t("tirage.mouvement.lieu")} />
                <AF.Text
                  name="dateDepart"
                  label={t("tirage.mouvement.dateDepart")}
                />
                <AF.Text
                  name="dateRetour"
                  label={t("tirage.mouvement.dateRetour")}
                />
                <AF.Checkbox
                  name="constatEtatDepart"
                  label={t("tirage.mouvement.constatEtatDepart")}
                />
                <AF.Checkbox
                  name="constatEtatRetour"
                  label={t("tirage.mouvement.constatEtatRetour")}
                />
                <AF.RichText
                  name="notes"
                  label={t("tirage.mouvement.notes")}
                />
              </AF.Col>
              <div className="flex gap-2 px-6 py-4 border-t border-gray-200">
                <SheetClose asChild>
                  <CancelButton className="flex-1" />
                </SheetClose>
                {!readOnly &&
                  (isEdit ? (
                    <UpdateButton
                      type="submit"
                      className="flex-1"
                      disabled={disabled}
                    />
                  ) : (
                    <SaveButton
                      type="submit"
                      className="flex-1"
                      disabled={disabled}
                    />
                  ))}
              </div>
            </form>
          </fieldset>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default CreateOrEditTirageMouvement;
